import { useState } from 'react'
import { Check, AlertCircle, Info } from 'lucide-react'
import { FormInput, SubmitButton } from '@/components/form'
import { Toggle } from '@/components/ui/Toggle'
import { useTranslation } from 'react-i18next'
import { facilitiesService } from '@/services/facilities'
import { getApiErrorMessage } from '@/utils/errors'
import { AxiosError } from 'axios'
import { formatPrice } from '@/utils/pricing'

export interface QuoteOptionalData {
  address: string
  city: string
  postalCode: string
  phone: string
}

interface FINESSVerificationStepProps {
  finessNumber: string
  onFinessNumberChange: (value: string) => void
  price: number | null
  selectedOfferName?: string | null
  onNext: () => void
  onFinessVerified: (data: {
    finess_number: string
    facility_name: string
    organization_legal_name?: string
  }) => void
  isQuoteFlow?: boolean
  isCustomOfferFlow?: boolean
  quoteAddress?: string
  quoteCity?: string
  quotePostalCode?: string
  quotePhone?: string
  onQuoteAddressChange?: (value: string) => void
  onQuoteCityChange?: (value: string) => void
  onQuotePostalCodeChange?: (value: string) => void
  onQuotePhoneChange?: (value: string) => void
}

export function FINESSVerificationStep({
  finessNumber = '',
  onFinessNumberChange,
  price,
  selectedOfferName = null,
  onNext,
  onFinessVerified,
  isQuoteFlow = false,
  isCustomOfferFlow = false,
  quoteAddress = '',
  quoteCity = '',
  quotePostalCode = '',
  quotePhone = '',
  onQuoteAddressChange,
  onQuoteCityChange,
  onQuotePostalCodeChange,
  onQuotePhoneChange,
}: FINESSVerificationStepProps) {
  const { t } = useTranslation('wizard')
  const [hasFiness, setHasFiness] = useState(true)
  const [organizationLegalName, setOrganizationLegalName] = useState('')
  const [isValidated, setIsValidated] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [facilityName, setFacilityName] = useState<string | null>(null)
  const [lastValidatedFiness, setLastValidatedFiness] = useState('')

  const handleFinessChange = (value: string) => {
    onFinessNumberChange(value)
    if (value !== lastValidatedFiness && isValidated) {
      setIsValidated(false)
      setFacilityName(null)
      setError(null)
    }
  }

  const handleNoFinessToggle = (checked: boolean) => {
    setHasFiness(!checked)
    if (checked) {
      setIsValidated(false)
      setError(null)
      onFinessNumberChange('')
      setLastValidatedFiness('')
      setFacilityName(null)
    } else {
      setOrganizationLegalName('')
    }
  }

  const handleValidate = async () => {
    if (hasFiness) {
      if (finessNumber.trim().length === 0) return

      setIsLoading(true)
      setError(null)

      try {
        const { data } = await facilitiesService.verifyFiness({
          finess_number: finessNumber.trim(),
        })

        const name = typeof data.name === 'string' ? data.name : ''
        setFacilityName(name)
        onFinessVerified({ finess_number: finessNumber.trim(), facility_name: name })
        setLastValidatedFiness(finessNumber.trim())
        setIsValidated(true)
      } catch (err) {
        const axiosError = err as AxiosError
        const status = axiosError.response?.status

        if (status === 404) {
          setError(getApiErrorMessage(err, t('finessVerification.errors.notFound')))
        } else {
          setError(getApiErrorMessage(err, t('finessVerification.errors.generic')))
        }
        setIsValidated(false)
      } finally {
        setIsLoading(false)
      }
    } else {
      const name = organizationLegalName.trim()
      if (name.length === 0) return
      setError(null)
      onFinessVerified({
        finess_number: '',
        facility_name: '',
        organization_legal_name: name,
      })
      setFacilityName(name)
      setIsValidated(true)
    }
  }

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (
      e.key === 'Enter' &&
      !isValidated &&
      (hasFiness ? finessNumber.trim().length > 0 : organizationLegalName.trim().length > 0)
    ) {
      e.preventDefault()
      handleValidate()
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!isValidated) {
      handleValidate()
    } else {
      onNext()
    }
  }

  const showSummary =
    isValidated &&
    facilityName !== null &&
    (hasFiness ? finessNumber === lastValidatedFiness : true)

  return (
    <form onSubmit={handleSubmit} className="mx-auto max-w-2xl">
      <div className="mb-10 rounded-2xl border border-[#E9E9E9] bg-white p-6 shadow-[0px_-1px_4px_rgba(0,0,0,0.25)]">
        <h2 className="mb-4 text-base font-medium text-black">
          {t('finessVerification.sectionTitle')}
        </h2>

        {error && (
          <div className="mb-4 flex items-start gap-3 rounded-lg border border-red-300 bg-red-50 p-4">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-red-600" />
            <p className="text-sm font-normal text-red-600">{error}</p>
          </div>
        )}

        <div className="mb-4">
          <Toggle
            rightLabel={t('finessVerification.noFinessToggle')}
            checked={!hasFiness}
            onCheckedChange={handleNoFinessToggle}
            uncheckedTrackClassName="bg-[#FFDFE6]"
            aria-label={t('finessVerification.noFinessToggle')}
          />
        </div>

        {hasFiness ? (
          <div>
            <label htmlFor="finess" className="mb-2 block text-sm font-semibold text-black">
              {t('finessVerification.label')}
              <span className="text-primary">*</span>
            </label>
            <input
              id="finess"
              type="text"
              value={finessNumber}
              onChange={e => handleFinessChange(e.target.value)}
              onKeyDown={handleInputKeyDown}
              placeholder={t('finessVerification.placeholder')}
              className={`mb-2 w-full rounded-lg px-4 py-3.5 ${
                isValidated && finessNumber === lastValidatedFiness
                  ? 'border border-[#41B95D] bg-[#EDF9F0]'
                  : 'border border-[#858C94] bg-white'
              }`}
              required={hasFiness}
            />
            {isValidated && hasFiness && finessNumber === lastValidatedFiness && (
              <div className="mb-4 flex items-center gap-2 rounded-lg bg-[#EDF9F0] px-4 py-2">
                <Check className="h-4 w-4 text-[#41B95D]" />
                <span className="text-sm font-normal text-[#41B95D]">
                  {t('finessVerification.validMessage')}
                </span>
              </div>
            )}
          </div>
        ) : (
          <div className="mb-4">
            <div
              id="no-finess-warning"
              className="mb-4 flex items-center gap-3 rounded-lg border-[0.3px] border-[#B45628] bg-[#FEFCEE] p-4"
            >
              <Info className="mt-0.5 h-5 w-5 shrink-0 text-[#B45628]" />
              <p className="text-[13px] font-normal text-[#B45628]">
                {t('finessVerification.noFinessWarning')}
              </p>
            </div>
            <FormInput
              id="organization-legal-name"
              label={t('finessVerification.legalNameLabel')}
              required
              type="text"
              value={organizationLegalName}
              onChange={e => setOrganizationLegalName(e.target.value)}
              placeholder={t('finessVerification.legalNamePlaceholder')}
            />
          </div>
        )}

        {showSummary && (
          <>
            <div className="mb-6 rounded-lg border border-[#F7F7F7] bg-[#F8F8F8] p-4">
              <div className="flex flex-wrap items-start gap-8">
                <div>
                  <div className="mb-1 text-sm font-normal text-gray-600">
                    {t('finessVerification.structureName')}
                  </div>
                  <div className="text-sm font-semibold text-black">{facilityName || '-'}</div>
                </div>
                <div>
                  <div className="mb-1 text-sm font-normal text-gray-600">
                    {t('finessVerification.selectedOffer')}
                  </div>
                  <div className="flex flex-wrap items-baseline gap-1 text-[14px] leading-normal font-medium">
                    {selectedOfferName ? (
                      <>
                        <span className="text-black">{selectedOfferName}</span>
                        {price !== null && price > 0 && (
                          <span className="text-[#B11739]">({formatPrice(price)}€)</span>
                        )}
                      </>
                    ) : (
                      <span className="text-black">-</span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {isQuoteFlow && !isCustomOfferFlow && (
              <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label
                    htmlFor="quote-address"
                    className="mb-2 block text-sm font-semibold text-black"
                  >
                    {t('finessVerification.quoteFields.address')}
                  </label>
                  <input
                    id="quote-address"
                    type="text"
                    value={quoteAddress}
                    onChange={e => onQuoteAddressChange?.(e.target.value)}
                    placeholder={t('finessVerification.quoteFields.addressPlaceholder')}
                    className="w-full rounded-lg border border-[#858C94] bg-white px-4 py-3.5"
                  />
                </div>
                <div>
                  <label
                    htmlFor="quote-city"
                    className="mb-2 block text-sm font-semibold text-black"
                  >
                    {t('finessVerification.quoteFields.city')}
                  </label>
                  <input
                    id="quote-city"
                    type="text"
                    value={quoteCity}
                    onChange={e => onQuoteCityChange?.(e.target.value)}
                    placeholder={t('finessVerification.quoteFields.cityPlaceholder')}
                    className="w-full rounded-lg border border-[#858C94] bg-white px-4 py-3.5"
                  />
                </div>
                <div>
                  <label
                    htmlFor="quote-postal-code"
                    className="mb-2 block text-sm font-semibold text-black"
                  >
                    {t('finessVerification.quoteFields.postalCode')}
                  </label>
                  <input
                    id="quote-postal-code"
                    type="text"
                    value={quotePostalCode}
                    onChange={e => onQuotePostalCodeChange?.(e.target.value)}
                    placeholder={t('finessVerification.quoteFields.postalCodePlaceholder')}
                    className="w-full rounded-lg border border-[#858C94] bg-white px-4 py-3.5"
                  />
                </div>
                <div>
                  <label
                    htmlFor="quote-phone"
                    className="mb-2 block text-sm font-semibold text-black"
                  >
                    {t('finessVerification.quoteFields.phone')}
                  </label>
                  <input
                    id="quote-phone"
                    type="tel"
                    value={quotePhone}
                    onChange={e => onQuotePhoneChange?.(e.target.value)}
                    placeholder={t('finessVerification.quoteFields.phonePlaceholder')}
                    className="w-full rounded-lg border border-[#858C94] bg-white px-4 py-3.5"
                  />
                </div>
              </div>
            )}
          </>
        )}

        <SubmitButton
          className="mt-6"
          isLoading={isLoading}
          loadingLabel={t('finessVerification.loading')}
          disabled={
            hasFiness ? finessNumber.trim().length === 0 : organizationLegalName.trim().length === 0
          }
        >
          {showSummary ? t('finessVerification.nextButton') : t('finessVerification.submitButton')}
        </SubmitButton>
      </div>
    </form>
  )
}
